import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptIcon,
  Remove as RemoveIcon,
  RestaurantMenu as RestaurantIcon,
} from "@mui/icons-material";
import { CartItem, CustomerDetails, Table } from "../types";
import { petpooja } from "../posPetpoojaTheme";
import { sumCartGst } from "../../../utils/gstTax";

interface CartPanelProps {
  selectedTable: Table | null;
  cartItems: CartItem[];
  billItems: CartItem[];
  pendingItems: CartItem[];
  customerDetails: CustomerDetails;
  activeSaleId: string | null;
  cartSubtotal: number;
  taxMode?: string | null;
  onUpdateQuantity: (itemId: string, delta: number) => void;
  onRemoveItem: (itemId: string) => void;
  onPrintKOT: () => void;
  onSettleBill: () => void;
  onEditCustomer: () => void;
  processing?: boolean;
}

const kotStatusLabel: Record<string, string> = {
  PENDING: "KOT Sent",
  PREPARING: "Preparing",
  READY: "Ready",
  SERVED: "Served",
};

const OrderLine: React.FC<{
  item: CartItem;
  idx: number;
  onUpdateQuantity: (itemId: string, delta: number) => void;
  onRemoveItem: (itemId: string) => void;
}> = ({ item, idx, onUpdateQuantity, onRemoveItem }) => (
  <Box
    sx={{
      px: 2,
      py: 1,
      bgcolor: idx % 2 === 0 ? petpooja.panel : petpooja.bg,
      borderBottom: `1px solid ${petpooja.border}`,
      opacity: item.locked ? 0.92 : 1,
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, gap: 1 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={700} color={petpooja.text}>
          {item.name}
        </Typography>
        {item.locked && (item.kotNumber || item.kotStatus) && (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
            {item.kotNumber && (
              <Chip
                label={item.kotNumber.replace(/^KOT-/, "KOT ")}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.6rem",
                  bgcolor: petpooja.accentLight,
                  color: petpooja.accentDark,
                  fontWeight: 700,
                }}
              />
            )}
            {item.kotStatus && (
              <Chip
                label={kotStatusLabel[item.kotStatus] ?? item.kotStatus}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.6rem",
                  bgcolor: petpooja.bg,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        )}
      </Box>
      {!item.locked && (
        <IconButton
          size="small"
          onClick={() => onRemoveItem(item.id)}
          sx={{ color: petpooja.red, p: 0.25, flexShrink: 0 }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      {item.locked ? (
        <Typography variant="body2" fontWeight={700} color={petpooja.textMuted}>
          Qty {item.quantity}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            border: `1px solid ${petpooja.border}`,
            borderRadius: 1,
            bgcolor: petpooja.panel,
          }}
        >
          <IconButton size="small" onClick={() => onUpdateQuantity(item.id, -1)}>
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography
            variant="body2"
            fontWeight={800}
            sx={{ minWidth: 28, textAlign: "center" }}
          >
            {item.quantity}
          </Typography>
          <IconButton size="small" onClick={() => onUpdateQuantity(item.id, 1)}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="caption" color={petpooja.textMuted} display="block">
          ₹{item.price.toFixed(0)} each
        </Typography>
        <Typography variant="body2" fontWeight={800} color={petpooja.greenDark}>
          ₹{(item.price * item.quantity).toFixed(0)}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const CartPanel: React.FC<CartPanelProps> = ({
  selectedTable,
  cartItems,
  billItems,
  pendingItems,
  customerDetails,
  activeSaleId,
  cartSubtotal,
  taxMode,
  onUpdateQuantity,
  onRemoveItem,
  onPrintKOT,
  onSettleBill,
  onEditCustomer,
  processing = false,
}) => {
  const itemCount = cartItems.reduce((sum, i) => sum + i.quantity, 0);
  const gst = sumCartGst(
    cartItems.map((item) => ({
      lineNet: item.price * item.quantity,
      gstPercentage: item.gstPercentage ?? 0,
      isTaxInclusive: Boolean(item.isTaxInclusive),
      taxMode,
    }))
  );
  const taxAmount = gst.taxAmount;
  const grandTotal = gst.netAmount;
  const taxLabel =
    taxMode === "IGST"
      ? "IGST"
      : taxAmount > 0
        ? "CGST+SGST"
        : "Tax";

  return (
    <Box
      sx={{
        width: { xs: 300, md: 360 },
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: petpooja.panel,
        borderLeft: `1px solid ${petpooja.border}`,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: petpooja.header,
          color: petpooja.headerText,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
          <ReceiptIcon sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {selectedTable ? selectedTable.name : "No Table Selected"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Dine In · {selectedTable ? `${selectedTable.seats} covers` : "—"}
            </Typography>
          </Box>
          {activeSaleId && (
            <Chip
              label="Running"
              size="small"
              sx={{
                bgcolor: petpooja.accent,
                color: "#fff",
                fontWeight: 700,
                height: 22,
                fontSize: "0.7rem",
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            bgcolor: "rgba(255,255,255,0.08)",
            borderRadius: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <PersonIcon sx={{ fontSize: 16, opacity: 0.8 }} />
          <Typography variant="caption" sx={{ flex: 1 }} noWrap>
            {customerDetails.name || "Walk-in Customer"}
            {customerDetails.phone ? ` · ${customerDetails.phone}` : ""}
          </Typography>
          <IconButton size="small" onClick={onEditCustomer} sx={{ color: "#fff", p: 0.25 }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 0.75,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${petpooja.border}`,
          bgcolor: petpooja.bg,
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" fontWeight={700} color={petpooja.text}>
          Order List
        </Typography>
        <Typography variant="caption" color={petpooja.textMuted}>
          {itemCount} qty · {cartItems.length} lines
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", bgcolor: petpooja.bg }}>
        {cartItems.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
            <RestaurantIcon sx={{ fontSize: 52, color: petpooja.border, mb: 1.5 }} />
            <Typography variant="body2" fontWeight={600} color={petpooja.textMuted}>
              Add items from menu
            </Typography>
            <Typography variant="caption" color={petpooja.textMuted}>
              Tap item to add · Send KOT to kitchen
            </Typography>
          </Box>
        ) : (
          <>
            {billItems.length > 0 && (
              <>
                <Box sx={{ px: 2, py: 0.5, bgcolor: petpooja.accentLight }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={petpooja.accentDark}
                  >
                    On Bill ({billItems.length})
                  </Typography>
                </Box>
                {billItems.map((item, idx) => (
                  <OrderLine
                    key={item.id}
                    item={item}
                    idx={idx}
                    onUpdateQuantity={onUpdateQuantity}
                    onRemoveItem={onRemoveItem}
                  />
                ))}
              </>
            )}
            {pendingItems.length > 0 && (
              <>
                <Box sx={{ px: 2, py: 0.5, bgcolor: petpooja.bg, borderTop: `1px solid ${petpooja.border}` }}>
                  <Typography variant="caption" fontWeight={700} color={petpooja.text}>
                    New Items ({pendingItems.length})
                  </Typography>
                </Box>
                {pendingItems.map((item, idx) => (
                  <OrderLine
                    key={item.id}
                    item={item}
                    idx={idx}
                    onUpdateQuantity={onUpdateQuantity}
                    onRemoveItem={onRemoveItem}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Box>

      <Box
        sx={{
          borderTop: `2px solid ${petpooja.border}`,
          flexShrink: 0,
          bgcolor: petpooja.panel,
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          {[
            ["Sub Total", cartSubtotal],
            [taxLabel, taxAmount],
          ].map(([label, val]) => (
            <Box
              key={label as string}
              sx={{ display: "flex", justifyContent: "space-between", py: 0.2 }}
            >
              <Typography variant="caption" color={petpooja.textMuted}>
                {label}
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                ₹{Number(val).toFixed(2)}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ my: 0.75 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle2" fontWeight={800}>
              Grand Total
            </Typography>
            <Typography variant="h6" fontWeight={800} color={petpooja.accent}>
              ₹{grandTotal.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0,
            borderTop: `1px solid ${petpooja.border}`,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={onPrintKOT}
            disabled={pendingItems.length === 0 || !activeSaleId || processing}
            sx={{
              borderRadius: 0,
              py: 1.5,
              bgcolor: petpooja.accent,
              fontWeight: 700,
              fontSize: "0.85rem",
              "&:hover": { bgcolor: petpooja.accentDark },
              "&.Mui-disabled": { bgcolor: "#f0f0f0" },
            }}
          >
            KOT
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<ReceiptIcon />}
            onClick={onSettleBill}
            disabled={!activeSaleId || cartItems.length === 0 || processing}
            sx={{
              borderRadius: 0,
              py: 1.5,
              bgcolor: petpooja.greenDark,
              fontWeight: 700,
              fontSize: "0.85rem",
              "&:hover": { bgcolor: petpooja.green },
              "&.Mui-disabled": { bgcolor: "#f0f0f0" },
            }}
          >
            Settle
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CartPanel;
